import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  formatAnalyticsReferenceDateLong,
  todayLocalYmd,
  useAnalyticsReferenceDate,
} from '../contexts/AnalyticsReferenceDateContext';
import { getApiErrorMessage } from '../utils/apiError';
import HabitAnalyticsSettingsSection from '../components/HabitAnalyticsSettingsSection';
import BackfillTimelineSettingsSection from '../components/BackfillTimelineSettingsSection';
import CheckInSettingsSection from '../components/CheckInSettingsSection';
import CatchUpOverviewSection from '../components/CatchUpOverviewSection';
import HourClassesSection from '../components/HourClassesSection';
import ScorePresetsSection from '../components/ScorePresetsSection';
import DisplayLayoutSettingsSection from '../components/DisplayLayoutSettingsSection';
import ThemeSettingsSection from '../components/ThemeSettingsSection';
import SessionsSection from '../components/SessionsSection';
import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { cardContentPaddingSx, pageGridSpacing, pageHeaderSx, sectionGapSx } =
    useLayoutTokens();
  const {
    asOfDate,
    enabled: analyticsReferenceDateEnabled,
    resetToToday,
    setAsOfDate,
  } = useAnalyticsReferenceDate();
  const [totalHabits, setTotalHabits] = useState(0);
  const [totalTimelines, setTotalTimelines] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [habitsRes, timelinesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/habits/`),
          axios.get(`${API_BASE_URL}/api/timelines/`),
        ]);
        setTotalHabits(habitsRes.data.length);
        setTotalTimelines(timelinesRes.data.length);
      } catch (err: any) {
        setError(getApiErrorMessage(err, 'Failed to load profile statistics'));
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const analyticsReferenceDateValue = asOfDate || todayLocalYmd();
  const analyticsReferenceDateLabel = analyticsReferenceDateEnabled
    ? `View analytics as of ${formatAnalyticsReferenceDateLong(asOfDate)}`
    : 'Use today';

  if (loading) {
    return (
      <PageShell maxWidth="narrow">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="narrow">
      <Box sx={pageHeaderSx}>
        <Typography variant="h3" component="h1" gutterBottom>
          Profile
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your account settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={pageGridSpacing}>
        <Grid item xs={12}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={user?.email || ''}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={user?.username || ''}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={user?.full_name || ''}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Button variant="contained" disabled>
                      Read-only During Stabilization
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h6" gutterBottom>
                Account Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {new Date(user?.created_at || '').toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Member Since
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="secondary">
                      {totalHabits}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Habits
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {totalTimelines}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Timelines
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <ThemeSettingsSection />
        </Grid>

        <Grid item xs={12}>
          <SessionsSection />
        </Grid>

        <Grid item xs={12}>
          <DisplayLayoutSettingsSection />
        </Grid>

        <Grid item xs={12}>
          <CheckInSettingsSection />
        </Grid>

        <Grid item xs={12}>
          <CatchUpOverviewSection />
        </Grid>

        <Grid item xs={12}>
          <ScorePresetsSection />
        </Grid>

        <Grid item xs={12}>
          <HourClassesSection />
        </Grid>

        <Grid item xs={12}>
          <BackfillTimelineSettingsSection />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent sx={cardContentPaddingSx}>
              <Typography variant="h6" gutterBottom>
                Advanced Analytics
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Analytics Reference Date
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Use today · View analytics as of a specific date
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {analyticsReferenceDateLabel}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={analyticsReferenceDateEnabled}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setAsOfDate(asOfDate || todayLocalYmd());
                      } else {
                        resetToToday();
                      }
                    }}
                    color="primary"
                  />
                }
                label="Use a historical reference date for analytics"
              />
              <Grid container spacing={2} sx={{ mt: 0.5 }} alignItems="center">
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Analytics Reference Date"
                    InputLabelProps={{ shrink: true }}
                    value={analyticsReferenceDateValue}
                    disabled={!analyticsReferenceDateEnabled}
                    onChange={(event) => setAsOfDate(event.target.value || null)}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={resetToToday}
                    disabled={!analyticsReferenceDateEnabled}
                  >
                    Reset to Today
                  </Button>
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Use this when reviewing imported backups or historical habit data. This changes
                analytics views only. It does not change habit logs, timers, reflections, or
                today's actual date.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Advanced Analytics: Habit Stats chart preferences */}
        <Grid item xs={12}>
          <HabitAnalyticsSettingsSection />
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default Profile;
